import { useQuery } from 'convex/react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../convex/_generated/api';
import { Button } from '@/components/ui/button';
import './OverviewDashboard.css';

export default function OverviewDashboard() {
  const navigate = useNavigate();

  // Fetch aggregate data
  const recentScans = useQuery(api.scans.getRecentScans, { limit: 5 });
  const monitoringStats = useQuery(api.monitoring.getMonitoringStats);
  const domainStats = useQuery(api.domainAnalytics.getDomainAnalyticsSummary);
  const totalScans = useQuery(api.scans.getTotalScansCount);
  const visibilityStats = useQuery(api.visibilityTrends.getAllVisibilityTrends, { limit: 100 });

  const isLoading = !recentScans || !monitoringStats || !domainStats || totalScans === undefined || !visibilityStats;

  if (isLoading) {
    return (
      <div className="overview-dashboard">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="overview-dashboard">
      {/* Quick Actions */}
      <div className="quick-actions">
        <Button onClick={() => navigate('/new-scan')} size="lg">
          🔍 Start New Scan
        </Button>
        <Button variant="outline" onClick={() => navigate('/monitoring')} size="lg">
          📊 View Monitoring
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon">📊</div>
          <div className="metric-content">
            <div className="metric-value">{totalScans}</div>
            <div className="metric-label">Total Scans</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">🎯</div>
          <div className="metric-content">
            <div className="metric-value">{domainStats.totalViolations.toLocaleString()}</div>
            <div className="metric-label">Total Violations</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">🌐</div>
          <div className="metric-content">
            <div className="metric-value">{domainStats.totalUniqueDomains}</div>
            <div className="metric-label">Unique Domains</div>
          </div>
        </div>

        <div className="metric-card highlight">
          <div className="metric-icon">⚠️</div>
          <div className="metric-content">
            <div className="metric-value">{domainStats.repeatOffendersCount}</div>
            <div className="metric-label">Repeat Offenders (2+)</div>
          </div>
        </div>

        <div className="metric-card warning">
          <div className="metric-icon">🚨</div>
          <div className="metric-content">
            <div className="metric-value">{domainStats.serialOffendersCount}</div>
            <div className="metric-label">Serial Offenders (5+)</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon">📈</div>
          <div className="metric-content">
            <div className="metric-value">{domainStats.avgViolationsPerDomain}</div>
            <div className="metric-label">Avg Violations/Domain</div>
          </div>
        </div>
      </div>

      {/* Monitoring Overview */}
      <div className="section">
        <div className="section-header">
          <h3>📺 Content Monitoring</h3>
          <Button variant="outline" size="sm" onClick={() => navigate('/monitoring')}>
            View All
          </Button>
        </div>

        <div className="monitoring-cards">
          <div className="monitor-card">
            <div className="card-icon">📚</div>
            <div className="card-content">
              <div className="card-value">{monitoringStats.total}</div>
              <div className="card-label">Titles Tracked</div>
            </div>
          </div>

          <div className="monitor-card success">
            <div className="card-icon">📉</div>
            <div className="card-content">
              <div className="card-value">{monitoringStats.declining || 0}</div>
              <div className="card-label">Declining</div>
            </div>
          </div>

          <div className="monitor-card">
            <div className="card-icon">➡️</div>
            <div className="card-content">
              <div className="card-value">{monitoringStats.stable || 0}</div>
              <div className="card-label">Stable</div>
            </div>
          </div>

          <div className="monitor-card warning">
            <div className="card-icon">📈</div>
            <div className="card-content">
              <div className="card-value">{monitoringStats.improving || 0}</div>
              <div className="card-label">Improving</div>
            </div>
          </div>
        </div>
      </div>

      {/* Search Visibility Overview */}
      <div className="section">
        <div className="section-header">
          <h3>📈 Search Visibility</h3>
          <Button variant="outline" size="sm" onClick={() => navigate('/visibility-trends')}>
            View All
          </Button>
        </div>

        <div className="monitoring-cards">
          <div className="monitor-card">
            <div className="card-icon">📚</div>
            <div className="card-content">
              <div className="card-value">{visibilityStats.totalTitles || 0}</div>
              <div className="card-label">Content Titles Tracked</div>
            </div>
          </div>

          <div className="monitor-card success">
            <div className="card-icon">📈</div>
            <div className="card-content">
              <div className="card-value">{visibilityStats.summary?.improving || 0}</div>
              <div className="card-label">Improving</div>
            </div>
          </div>

          <div className="monitor-card">
            <div className="card-icon">➡️</div>
            <div className="card-content">
              <div className="card-value">{visibilityStats.summary?.stable || 0}</div>
              <div className="card-label">Stable</div>
            </div>
          </div>

          <div className="monitor-card warning">
            <div className="card-icon">📉</div>
            <div className="card-content">
              <div className="card-value">{visibilityStats.summary?.declining || 0}</div>
              <div className="card-label">Declining</div>
            </div>
          </div>

          <div className="monitor-card highlight">
            <div className="card-icon">⭐</div>
            <div className="card-content">
              <div className="card-value">{visibilityStats.summary?.avgScore || 0}</div>
              <div className="card-label">Average Score</div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Scans */}
      <div className="section">
        <div className="section-header">
          <h3>🕒 Recent Scans</h3>
          <Button variant="outline" size="sm" onClick={() => navigate('/history')}>
            View History
          </Button>
        </div>

        {recentScans.length === 0 ? (
          <div className="empty-state">
            <p>No scans yet. Start your first scan to detect piracy!</p>
            <Button onClick={() => navigate('/new-scan')}>Start New Scan</Button>
          </div>
        ) : (
          <div className="recent-scans-list">
            {recentScans.map((scan) => (
              <div
                key={scan._id}
                className="scan-item"
                onClick={() => navigate(`/scan/${scan.batchId}`)}
              >
                <div className="scan-info">
                  <div className="scan-title">{scan.contentTitle}</div>
                  <div className="scan-meta">
                    {new Date(scan.startedAt).toLocaleString()}
                    {scan.contentYear && <span> • {scan.contentYear}</span>}
                    {scan.contentType && <span> • {scan.contentType}</span>}
                  </div>
                </div>
                <div className="scan-stats">
                  <div className={`status-badge status-${scan.status}`}>
                    {scan.status}
                  </div>
                  <div className="violation-count">
                    {scan.violationCount} violations
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
